import React, { useState, useEffect } from "react";
import ModalElement from "../../../Common/ModalElement";
import InputElement from "../../../Common/InputElement";
import ButtonElement from "../../../Common/ButtonElement";
import PermissionTable from "../../../Common/PermissionTable";
import { useApiRequest } from "../../../hooks/useApiRequest";
import { appApis } from "../../../constants";
import { toast } from "react-toastify";

const RoleModal = ({ show, onHide, role = {}, pages = [], refreshRoles }) => {
  const [roleName, setRoleName] = useState("");
  const [perms, setPerms] = useState({});
  const isEdit = !!role?.role_id;
  const { request: apiRequest, loading } = useApiRequest();

  // Helper: Get all descendant page_ids for a page
  const getAllDescendantPageIds = (page) => {
    let ids = [page.page_id];
    if (page.children && page.children.length > 0) {
      page.children.forEach(child => {
        ids = ids.concat(getAllDescendantPageIds(child));
      });
    }
    return ids;
  };

  // Helper: Find parent page_id for a given page_id
  const buildParentMap = (pagesList, parent = null, map = {}) => {
    pagesList.forEach(page => {
      map[page.page_id] = parent;
      if (page.children && page.children.length > 0) {
        buildParentMap(page.children, page.page_id, map);
      }
    });
    return map;
  };
  const parentMap = buildParentMap(pages);

  const handleCheckboxChange = (page, checked) => {
    setPerms(prev => {
      let updated = { ...prev };
      // If parent is checked/unchecked, check/uncheck all children recursively
      const allDescendants = getAllDescendantPageIds(page);
      allDescendants.forEach(id => {
        updated[id] = checked;
      });
      // If checking a child, check all its parents
      if (checked) {
        let parentId = parentMap[page.page_id];
        while (parentId) {
          updated[parentId] = true;
          parentId = parentMap[parentId];
        }
      } else {
        // If unchecking, and all siblings are unchecked, uncheck parent recursively
        let parentId = parentMap[page.page_id];
        while (parentId) {
          // Find the parent page object
          const parentPage = findPageById(pages, parentId);
          // If any child of this parent is still checked, stop
          if (parentPage.children.some(child => updated[child.page_id])) {
            break;
          }
          updated[parentId] = false;
          parentId = parentMap[parentId];
        }
      }
      return updated;
    });
  };

  // Helper: Find a page by id in the nested pages structure
  const findPageById = (pagesList, id) => {
    for (const page of pagesList) {
      if (page.page_id === id) return page;
      if (page.children) {
        const found = findPageById(page.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper to flatten all page ids
  const getAllPageIds = (pagesList) => {
    let ids = [];
    pagesList.forEach(page => {
      ids.push(page.page_id);
      if (page.children && page.children.length > 0) {
        ids = ids.concat(getAllPageIds(page.children));
      }
    });
    return ids;
  };

  const allPageIds = getAllPageIds(pages);
  const allChecked = allPageIds.length > 0 && allPageIds.every(id => perms[id]);

  const handleCheckAll = () => {
    if (allChecked) {
      // Uncheck all
      const cleared = {};
      allPageIds.forEach(id => { cleared[id] = false; });
      setPerms(cleared);
    } else {
      // Check all
      const filled = {};
      allPageIds.forEach(id => { filled[id] = true; });
      setPerms(filled);
    }
  };

  const handleSubmit = () => {
    const trimmedName = roleName.trim();
    const permissions = Object.keys(perms)
      .filter(pageId => perms[pageId])
      .map(pageId => ({ page_id: Number(pageId), status: true }));

    if (!trimmedName) {
      toast.error("Role name is required.", { autoClose: 3000 });
      return;
    }
    if (!permissions.length) {
      toast.error("At least one permission must be selected.", { autoClose: 3000 });
      return;
    }
    const payload = {
      role_name: trimmedName,
      permissions
    };
    apiRequest({
      url: isEdit ? `${appApis.updateRole}/${role.role_id}` : appApis.addRole,
      method: "POST",
      body: payload,
      showToast: true,
      onSuccess: () => {
        if (refreshRoles) refreshRoles();
        onHide();
      }
    });
  };

  useEffect(() => {
    if (isEdit && role) {
      setRoleName(role.role_name || role.name || "");
      const selected = {};
      (role.page_ids || []).forEach(id => { selected[id] = true; });
      setPerms(selected);
    } else {
      setRoleName("");
      setPerms({});
    }
  }, [show, isEdit, role]);

  return (
            <ModalElement height={"90vh"} FooterComponent={<div className="d-flex justify-content-end gap-2">
      <ButtonElement text={isEdit ? "Close" : "Cancel"} variant="btn-outline-danger" cssClass="me-2" onClick={onHide} />
      <ButtonElement text={isEdit ? "Submit" : "Create Role"} variant="btn-primary" onClick={handleSubmit} loading={loading} />
    </div>} show={show} onHide={onHide} title={isEdit ? "Edit Role" : "Create New Role"}>
      <div className="text-start">
        <InputElement
          disable={isEdit}
          placeholder="Enter Role"
          label={"Role Name"}
          value={roleName}
          onChange={e => setRoleName(e.target.value)}
          isRequired={true}
          style={{ marginBottom: 24, fontFamily: 'Inter, sans-serif', fontWeight: 500, color: '#48505E' }}
        />
      </div>
      <PermissionTable
        pages={pages}
        perms={perms}
        onCheckboxChange={handleCheckboxChange}
        allChecked={allChecked}
        onCheckAll={handleCheckAll}
      />
    </ModalElement>
  );
};

export default RoleModal; 
